import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import type { MutualFund } from "@/types/mutualFund";
import type { MutualFundDividendFormData } from "@/types/mutualFundDividend";
import type { Account } from "@accountHooks";
import { formatAccountLabel } from "./formatAccountLabel";
import { useCurrencyList } from "@/hooks/useCurrencyList";

export const dividendFormSchema = z.object({
  amount: z
    .number({ required_error: "Amount is required" })
    .gt(0, "Amount must be greater than 0")
    .max(999_999_999.99, "Amount must not exceed 999,999,999.99"),
  dividend_per_unit: z
    .number({ required_error: "Dividend per unit is required" })
    .gt(0, "Dividend per unit must be greater than 0")
    .max(999_999.999999, "Dividend per unit must not exceed 999,999.999999"),
  payment_date: z
    .string()
    .min(1, "Payment date is required")
    .refine(
      (d) => d <= new Date().toISOString().slice(0, 10),
      "Payment date must be today or in the past",
    ),
  currency: z.string().min(1, "Currency is required"),
  payout_account_id: z.string().nullable().default(null),
});

type DividendFormValues = z.infer<typeof dividendFormSchema>;

interface MutualFundDividendFormProps {
  fund: MutualFund;
  accounts: Account[];
  accountsLoading: boolean;
  accountsError: boolean;
  onSave: (data: MutualFundDividendFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function MutualFundDividendForm({
  fund,
  accounts,
  accountsLoading,
  accountsError,
  onSave,
  onCancel,
  isSubmitting = false,
}: MutualFundDividendFormProps) {
  const form = useForm<DividendFormValues>({
    resolver: zodResolver(dividendFormSchema),
    defaultValues: {
      amount: undefined,
      dividend_per_unit: undefined,
      payment_date: "",
      currency: fund.currency ?? "",
      payout_account_id: null,
    },
  });
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();
  const selectedPayoutAccountId = form.watch("payout_account_id");
  const selectedCurrency = form.watch("currency");
  const selectedAccount = accounts.find(
    (a) => a.id === selectedPayoutAccountId,
  );
  const showCurrencyMismatch =
    selectedAccount != null && selectedAccount.currency !== selectedCurrency;

  const onSubmit = async (values: DividendFormValues) => {
    await onSave({
      investment_account_id: fund.id,
      amount: values.amount,
      dividend_per_unit: values.dividend_per_unit,
      payment_date: values.payment_date,
      currency: values.currency,
      payout_account_id: values.payout_account_id,
    });
  };

  const payoutSelectorDisabled =
    accountsLoading || accountsError || accounts.length === 0;

  const payoutPlaceholder = accountsLoading
    ? "Loading accounts…"
    : accounts.length === 0
      ? "No accounts available"
      : "Select account (optional)";

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        aria-label="Record mutual fund dividend">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Total Amount *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="e.g. 1500.00"
                  aria-required="true"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === ""
                        ? undefined
                        : parseFloat(e.target.value),
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dividend_per_unit"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dividend per Unit *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.000001"
                  min="0.000001"
                  placeholder="e.g. 0.500000"
                  aria-required="true"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === ""
                        ? undefined
                        : parseFloat(e.target.value),
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="payment_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payment Date *</FormLabel>
              <FormControl>
                <Input
                  type="date"
                  aria-required="true"
                  max={new Date().toISOString().slice(0, 10)}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger
                    aria-required="true"
                    aria-label="Select currency">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {isLoadingCurrencies && "Loading Currencies"}
                  {!isLoadingCurrencies && !errorLoadingCurrencies ? (
                    <SelectContent>
                      {availableCurrencies.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  ) : (
                    "Failed to load currencies"
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="payout_account_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payout Account</FormLabel>
              <Select
                onValueChange={(val) =>
                  field.onChange(val === "__none__" ? null : val)
                }
                value={field.value ?? "__none__"}
                disabled={payoutSelectorDisabled}>
                <FormControl>
                  <SelectTrigger aria-label="Select payout account">
                    <SelectValue placeholder={payoutPlaceholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {formatAccountLabel(account)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {accountsError && (
                <Alert variant="destructive" className="mt-2">
                  <AlertDescription>
                    Failed to load accounts. You can still submit without a
                    payout account.
                  </AlertDescription>
                </Alert>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {showCurrencyMismatch && (
          <Alert variant="default">
            <AlertDescription>
              The selected account's currency ({selectedAccount.currency})
              differs from the dividend currency ({selectedCurrency}). You can
              still submit.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {isSubmitting ? "Saving…" : "Record Dividend"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
